import React, { useState, type FC, type PropsWithChildren, useContext, type UIEvent } from 'react';

import { getColor, isDark } from '@lucid/theme';
import { useCarouselScroll, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Container from '../container/container';
import ControlButton from '../buttons/control-button';
import CarouselCard from '../cards/carousel-card/carousel-card';
import LayoutContext from '../context/layout-context';
import Heading from '../heading/heading';
import Tab from '../tab/tab';

export interface CardCarouselProps {
  /** Allows the component to get additional styles. */
  cardCarouselItems: Partial<Lucid.Directus.Generated.Card_Carousel_Item>[];
  /** The heading for the Card Carousel section */
  cardCarouselSectionHeading?: string;
  /** The background color of the Card Carousel section e.g. indigo.10 */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Heading variant for the section title */
  headingVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** The style variant of the tab */
  tabVariant?: 'primary' | 'primaryGrey' | 'secondary' | 'secondaryGrey';
}

const CardCarousel: FC<PropsWithChildren<CardCarouselProps>> = ({
  cardCarouselItems,
  cardCarouselSectionHeading,
  backgroundColor = 'coolGrey.5',
  tabVariant = 'primary',
  headingVariant = 'headingXL',
}) => {
  const { product, shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_template_aria_label_slide_title',
      'mktg_homepage_learn_more_about',
      'mktg_homepage_cards_integrates_with',
      'mktg_carousel_slide_previous',
      'mktg_carousel_slide_next',
    ],
    shortCode,
  });
  const backgroundColorValue = typeof backgroundColor === 'object' ? backgroundColor[product] : backgroundColor;
  const hasDarkBackground = isDark(backgroundColorValue);
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollRef, handleScroll, updateCarousel } = useCarouselScroll({
    minItems: 1,
    numSlides: cardCarouselItems.length,
    handleUpdateCarousel: setActiveIndex,
    itemWidth: 384,
  });

  const handleTitleSelect = (index: number) => {
    updateCarousel(index);
    const divRef = document.getElementById(`cardCarouselItem-${index}`);
    divRef?.focus();
  };

  const cardCarouselTitleArray = cardCarouselItems.map(item => item.heading) as string[];

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const currentEl = e.target as HTMLElement;
    const divRef = document.getElementById(`cardCarouselItem-${index}`);

    let idx = 0;

    switch (e.code) {
      case 'ArrowDown':
        e.preventDefault();
        idx = (activeIndex + 1) % cardCarouselItems.length;
        updateCarousel(idx);
        (currentEl.parentNode?.children[idx] as HTMLElement).focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        idx = activeIndex === 0 ? cardCarouselItems.length - 1 : activeIndex - 1;
        updateCarousel(idx);
        (currentEl.parentNode?.children[idx] as HTMLElement).focus();
        break;
      case 'Enter':
        divRef?.focus();
        break;
      case 'Space':
        divRef?.focus();
        break;
    }
  };

  const increment = (index: number) => (index < cardCarouselItems.length - 1 ? index + 1 : 0);
  const decrement = (index: number) => (index > 0 ? index - 1 : cardCarouselItems.length - 1);

  return (
    <Box
      css={theme => ({
        label: 'cardCarousel',
        paddingTop: theme.space.s64,
        paddingBottom: theme.space.s120,
        [theme.breakpoints.bp500.min]: {
          paddingTop: theme.space.s120,
          paddingBottom: theme.space.s120,
        },
        overflow: 'hidden',
        backgroundColor: getColor({
          color: backgroundColor,
          theme,
        }),
      })}
    >
      <Container>
        <Heading
          as="h2"
          css={theme => ({ marginBottom: theme.space.s64 })}
          highlightTextColor="primary"
          text={cardCarouselSectionHeading}
          variant={headingVariant}
        />
        <Box css={theme => ({ [theme.breakpoints.bp900.min]: { marginTop: theme.space.s64, position: 'relative' } })}>
          <Box
            css={theme => ({
              display: 'grid',
              gridTemplateColumns: '1fr',
              minHeight: '100%',
              width: '100%',
              position: 'relative',
              top: theme.space.s8,
              [theme.breakpoints.bp900.min]: {
                gridTemplateColumns: '400px 1fr',
              },
            })}
          >
            <Box
              aria-orientation="vertical"
              css={theme => ({
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: `-${theme.space.s8}px`,
                  top: 0,
                  bottom: 0,
                  width: '100vw',
                  backgroundColor: getColor({
                    color: backgroundColor,
                    theme,
                  }),
                  borderRight: '1px solid',
                  borderColor: 'transparent',
                  zIndex: 3,
                  [theme.breakpoints.bp900.min]: {
                    borderColor: theme.colors.coolGrey[20],
                  },
                },
                paddingRight: theme.space.s24,
                paddingTop: theme.space.s8,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                rowGap: theme.space.s12,
              })}
              role="tablist"
            >
              {cardCarouselTitleArray?.map((titleItem, index) => {
                const titleItemKey = `titleItemKey-${index}`;
                const isActiveTitle = activeIndex === index;

                return (
                  <Tab
                    key={titleItemKey}
                    aria-controls={`cardCarouselItem-${index}`}
                    aria-label={withSmartling('mktg_template_aria_label_slide_title', { title: titleItem || '' })}
                    aria-selected={isActiveTitle}
                    id={`cardCarouselTitle-${index}`}
                    isActive={isActiveTitle}
                    label={titleItem}
                    onClick={() => updateCarousel(index)}
                    onFocus={() => updateCarousel(index)}
                    onKeyDown={e => handleKeyDown(e, index)}
                    role="tab"
                    variant="primary"
                  />
                );
              })}
            </Box>
            <Box
              ref={scrollRef}
              aria-live="polite"
              css={theme => ({
                overflowX: 'scroll',
                scrollSnapType: 'x mandatory',
                width: '100vw',
                gridAutoFlow: 'column',
                display: 'flex',
                height: 'fit-content',
                columnGap: theme.space.s24,
                zIndex: 1,
                marginLeft: '-1.875rem',
                padding: `${theme.space.s24}px 85vw ${theme.space.s24}px 1.875rem`,
                scrollPaddingInlineStart: '1.875rem',
                [theme.breakpoints.bp900.min]: {
                  padding: `${theme.space.s8}px 85vw ${theme.space.s24}px 1.875rem`,
                  marginLeft: 0,
                },
                [theme.breakpoints.bp1024.min]: {
                  width: 'calc(100vw - 400px)',
                  paddingRight: 'calc(100vw - 400px)',
                },
              })}
              id="cardCarouselItemContainer"
              onScroll={e => {
                handleScroll(e as UIEvent<HTMLDivElement>);
              }}
            >
              {cardCarouselItems.map((card, index) => {
                const integrationIcons = card?.integration_icons?.map(ii => ii.directus_files_id);
                const isActiveCard = index === activeIndex;

                return (
                  <CarouselCard
                    key={`cardCarousel-${index + 1}`}
                    aria-roledescription="slide"
                    bulletPoints={card.bullet_points}
                    handleUpdateCarousel={updateCarousel}
                    heading={card.heading}
                    index={index}
                    integrationIcons={integrationIcons as Lucid.Directus.Generated.Directus_Files[]}
                    integrationText={withSmartling('mktg_homepage_cards_integrates_with') || 'Integrates with:'}
                    isActiveCard={isActiveCard}
                    isGreyTab={tabVariant.includes('Grey')}
                    link={{
                      ...card.link,
                      id: `${card.link?.id}-${index + 1}`,
                      accessibleDescription: withSmartling('mktg_homepage_learn_more_about', {
                        title: card.heading || '',
                      }),
                    }}
                    media={{
                      image: card.media,
                    }}
                    onFocus={event => {
                      event.preventDefault();
                      updateCarousel(index);
                    }}
                    onKeyDown={event => {
                      if (event.code === 'Tab' && event.shiftKey === true) {
                        event.preventDefault();
                        const titleItem = document.getElementById(`cardCarouselTitle-${index}`);
                        titleItem?.focus();
                      }

                      if (event.code === 'ArrowRight') {
                        event.preventDefault();
                        handleTitleSelect(increment(index));
                      }

                      if (event.code === 'ArrowLeft') {
                        event.preventDefault();
                        handleTitleSelect(decrement(index));
                      }
                    }}
                    role="tabpanel"
                    tabIndex={index === activeIndex ? 0 : -1}
                    text={{ value: card.text }}
                  />
                );
              })}
            </Box>
            <Box
              css={theme => ({
                position: 'absolute',
                right: 0,
                bottom: `-${theme.space.s64}px`,
                display: 'flex',
                justifyContent: 'end',
                columnGap: theme.space.s24,
                marginRight: theme.space.s16,
                marginTop: theme.space.s32,
                [theme.breakpoints.bp900.min]: {
                  paddingTop: 0,
                  marginRight: theme.space.s32,
                  bottom: `-${theme.space.s80}px`,
                },
              })}
            >
              <ControlButton
                aria-label={(
                  withSmartling('mktg_carousel_slide_previous', {
                    slide: `${activeIndex === 0 ? 1 : activeIndex}`,
                  }) || 'Go to previous slide'
                ).concat(' Press Up to view card')}
                css={{ transform: 'rotate(180deg)', gridArea: 'backButton' }}
                hasDarkBackground={hasDarkBackground}
                iconType="ArrowRight"
                isDisabled={activeIndex === 0}
                onClick={() => updateCarousel(decrement(activeIndex))}
                onKeyDown={event => {
                  if (['Enter', 'Space'].includes(event.code)) {
                    event.preventDefault();
                    updateCarousel(decrement(activeIndex));
                  }

                  if (['ArrowUp'].includes(event.code)) {
                    event.preventDefault();
                    handleTitleSelect(activeIndex);
                  }
                }}
              />
              <ControlButton
                aria-label={(
                  withSmartling('mktg_carousel_slide_next', {
                    slide: `${activeIndex < cardCarouselItems.length - 1 ? activeIndex + 2 : 1}`,
                  }) || 'Go to next slide'
                ).concat(' Press Up to view card')}
                css={{ gridArea: 'nextButton' }}
                hasDarkBackground={hasDarkBackground}
                iconType="ArrowRight"
                onClick={() => {
                  updateCarousel(increment(activeIndex));
                }}
                onKeyDown={event => {
                  if (['Enter', 'Space'].includes(event.code)) {
                    event.preventDefault();
                    updateCarousel(increment(activeIndex));
                  }

                  if (['ArrowUp'].includes(event.code)) {
                    event.preventDefault();
                    handleTitleSelect(activeIndex);
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export { CardCarousel };
export default CardCarousel;
